/**
 * Per-country address conventions: ONE table for the wizard's labels and
 * placeholders and for the rulebook's postal-code verdict on both runtimes.
 *
 * Countries without postal codes (spec B4). This set is the countries with no
 * national postcode system, or whose addresses in practice carry none. It
 * comes from the UPU Postal Addressing Systems country sheets, cross-checked
 * against Google libaddressinput (countries with no `zip` field). Hong Kong,
 * the UAE and Qatar were the live-review cases (2026-09-21). Ireland is listed
 * because Eircodes (2015) are still routinely left off addresses; an Eircode
 * that IS entered is still checked against its format.
 *
 * No official Wyoming, Delaware or IRS rule covers a foreign address without a
 * postcode (spec research), so "optional" is our product rule. The persisted
 * shareholder row stores null.
 */

export interface CountryAddressMeta {
  readonly code: string;
  /** Label for the state/province field. */
  readonly regionLabel: string;
  /** Label for the postal-code field, without "(optional)". */
  readonly postalLabel: string;
  /** Placeholder and format example; null where no format is enforced. */
  readonly postalExample: string | null;
  /** Tested against the trimmed, upper-cased value. */
  readonly postalPattern: RegExp | null;
  /** Overrides the "<label> must look like <example>" copy. */
  readonly postalFormatMessage: string | null;
  readonly postalOptional: boolean;
  /** City placeholder example (ob-04 D3); null shows no example. */
  readonly cityExample: string | null;
}

type MetaOverride = Partial<
  Omit<CountryAddressMeta, "code" | "postalOptional">
>;

export const US_ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

export const POSTAL_REQUIRED_MESSAGE = "Postal/ZIP code is required";

const MIN_POSTAL_LENGTH = 2;
const MAX_POSTAL_LENGTH = 20;

export const POSTAL_LENGTH_MESSAGE = `Postal code must be between ${MIN_POSTAL_LENGTH} and ${MAX_POSTAL_LENGTH} characters`;

export const NO_POSTAL_CODE_COUNTRIES: ReadonlySet<string> = new Set([
  "AE",
  "AG",
  "AO",
  "AW",
  "BF",
  "BI",
  "BJ",
  "BO",
  "BS",
  "BW",
  "BZ",
  "CD",
  "CF",
  "CG",
  "CI",
  "CK",
  "CM",
  "DJ",
  "DM",
  "ER",
  "FJ",
  "GA",
  "GD",
  "GH",
  "GM",
  "GQ",
  "GY",
  "HK",
  "IE",
  "KI",
  "KM",
  "KN",
  "KP",
  "LC",
  "LY",
  "ML",
  "MO",
  "MR",
  "MW",
  "NR",
  "NU",
  "QA",
  "RW",
  "SB",
  "SC",
  "SL",
  "SR",
  "ST",
  "SY",
  "TD",
  "TF",
  "TG",
  "TK",
  "TL",
  "TO",
  "TT",
  "TV",
  "UG",
  "VU",
  "YE",
  "ZW",
]);

const DEFAULT_META: Omit<CountryAddressMeta, "code" | "postalOptional"> = {
  regionLabel: "State/Province/Region",
  postalLabel: "Postal code",
  postalExample: null,
  postalPattern: null,
  postalFormatMessage: null,
  cityExample: null,
};

const OVERRIDES: Readonly<Record<string, MetaOverride>> = {
  US: {
    cityExample: "Austin",
    regionLabel: "State",
    postalLabel: "ZIP code",
    postalExample: "94105",
    postalPattern: US_ZIP_PATTERN,
    postalFormatMessage: "US ZIP code must be in format 12345 or 12345-6789",
  },
  CA: {
    cityExample: "Toronto",
    regionLabel: "Province",
    postalExample: "K1A 0B1",
    postalPattern: /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/,
  },
  GB: {
    cityExample: "London",
    regionLabel: "County",
    postalLabel: "Postcode",
    postalExample: "SW1A 1AA",
    postalPattern: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/,
  },
  IE: {
    cityExample: "Dublin",
    regionLabel: "County",
    postalLabel: "Eircode",
    postalExample: "D02 X285",
    postalPattern: /^[A-Z]\d[\dW] ?[A-Z\d]{4}$/,
  },
  AU: {
    cityExample: "Sydney",
    regionLabel: "State",
    postalLabel: "Postcode",
    postalExample: "2000",
    postalPattern: /^\d{4}$/,
  },
  DE: {
    cityExample: "Berlin",
    regionLabel: "State",
    postalExample: "10115",
    postalPattern: /^\d{5}$/,
  },
  FR: {
    cityExample: "Paris",
    regionLabel: "Region",
    postalExample: "75001",
    postalPattern: /^\d{5}$/,
  },
  IT: {
    cityExample: "Milan",
    regionLabel: "Province",
    postalExample: "00118",
    postalPattern: /^\d{5}$/,
  },
  ES: {
    cityExample: "Madrid",
    regionLabel: "Province",
    postalExample: "28001",
    postalPattern: /^\d{5}$/,
  },
  NL: {
    cityExample: "Amsterdam",
    regionLabel: "Province",
    postalExample: "1011 AB",
    postalPattern: /^\d{4} ?[A-Z]{2}$/,
  },
  CH: {
    cityExample: "Zurich",
    regionLabel: "Canton",
    postalExample: "8001",
    postalPattern: /^\d{4}$/,
  },
  PL: {
    cityExample: "Warsaw",
    regionLabel: "Voivodeship",
    postalExample: "00-001",
    postalPattern: /^\d{2}-\d{3}$/,
  },
  PT: {
    cityExample: "Lisbon",
    regionLabel: "District",
    postalExample: "1000-001",
    postalPattern: /^\d{4}-\d{3}$/,
  },
  TR: {
    cityExample: "Istanbul",
    regionLabel: "Province",
    postalExample: "34000",
    postalPattern: /^\d{5}$/,
  },
  RU: {
    cityExample: "Moscow",
    regionLabel: "Region",
    postalExample: "101000",
    postalPattern: /^\d{6}$/,
  },
  IL: {
    cityExample: "Tel Aviv",
    regionLabel: "District",
    postalExample: "9100001",
    postalPattern: /^\d{7}$/,
  },
  SA: {
    cityExample: "Riyadh",
    regionLabel: "Region",
    postalExample: "11564",
    postalPattern: /^\d{5}(-\d{4})?$/,
  },
  PK: {
    cityExample: "Lahore",
    regionLabel: "Province",
    postalExample: "44000",
    postalPattern: /^\d{5}$/,
  },
  IN: {
    cityExample: "Mumbai",
    regionLabel: "State",
    postalLabel: "PIN code",
    postalExample: "110001",
    postalPattern: /^\d{6}$/,
  },
  CN: {
    cityExample: "Shanghai",
    regionLabel: "Province",
    postalExample: "100000",
    postalPattern: /^\d{6}$/,
  },
  JP: {
    cityExample: "Tokyo",
    regionLabel: "Prefecture",
    postalExample: "100-0001",
    postalPattern: /^\d{3}-?\d{4}$/,
  },
  KR: {
    cityExample: "Seoul",
    regionLabel: "Province",
    postalExample: "03051",
    postalPattern: /^\d{5}$/,
  },
  SG: {
    cityExample: "Singapore",
    regionLabel: "Region",
    postalExample: "018956",
    postalPattern: /^\d{6}$/,
  },
  BR: {
    cityExample: "São Paulo",
    regionLabel: "State",
    postalLabel: "CEP",
    postalExample: "01001-000",
    postalPattern: /^\d{5}-?\d{3}$/,
  },
  MX: {
    cityExample: "Mexico City",
    regionLabel: "State",
    postalExample: "06000",
    postalPattern: /^\d{5}$/,
  },
  AE: { regionLabel: "Emirate", cityExample: "Dubai" },
  HK: { regionLabel: "District", cityExample: "Kowloon" },
  QA: { regionLabel: "Municipality", cityExample: "Doha" },
};

export function countryAddressMeta(
  country: string | null | undefined,
): CountryAddressMeta {
  const code = typeof country === "string" ? country.trim().toUpperCase() : "";
  return {
    ...DEFAULT_META,
    ...(OVERRIDES[code] ?? {}),
    code,
    postalOptional: NO_POSTAL_CODE_COUNTRIES.has(code),
  };
}

export function isPostalCodeOptional(
  country: string | null | undefined,
): boolean {
  return countryAddressMeta(country).postalOptional;
}

/**
 * Why a postal code cannot be right for the country, or null when it can.
 * Never carries the value, so it is safe in a 400 body.
 */
export function postalCodeIssue(
  country: string | null | undefined,
  zipCode: string | null | undefined,
): string | null {
  const meta = countryAddressMeta(country);
  const value = typeof zipCode === "string" ? zipCode.trim() : "";

  if (value.length === 0) {
    return meta.postalOptional ? null : POSTAL_REQUIRED_MESSAGE;
  }

  if (meta.postalPattern !== null) {
    if (meta.postalPattern.test(value.toUpperCase())) return null;
    return (
      meta.postalFormatMessage ??
      `${meta.postalLabel} must look like ${meta.postalExample ?? ""}`.trim()
    );
  }

  return value.length >= MIN_POSTAL_LENGTH && value.length <= MAX_POSTAL_LENGTH
    ? null
    : POSTAL_LENGTH_MESSAGE;
}
